import React, { useState, useEffect } from 'react';
import './App.css';
import { ShipCanvas } from './ShipCanvas';
import { ControlPanel } from './ControlPanel';
import { ShipCustomization, ShipDesign } from './types';
import { storageUtils } from './storageUtils';

function App() {
  const [customization, setCustomization] = useState<ShipCustomization>(
    storageUtils.getCurrentCustomization()
  );
  const [savedDesigns, setSavedDesigns] = useState<ShipDesign[]>(storageUtils.getDesigns());
  const [designName, setDesignName] = useState('');
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);

  useEffect(() => {
    storageUtils.saveCurrentCustomization(customization);
  }, [customization]);

  const handleSaveDesign = () => {
    if (!designName.trim()) {
      alert('Please enter a design name');
      return;
    }

    const newDesign: ShipDesign = {
      id: Date.now().toString(),
      name: designName,
      ...customization,
      createdAt: Date.now(),
    };

    storageUtils.saveDesign(newDesign);
    setSavedDesigns(storageUtils.getDesigns());
    setDesignName('');
    alert(`Design "${designName}" saved!`);
  };

  const handleLoadDesign = (design: ShipDesign) => {
    const { name, createdAt, ...designCustomization } = design;
    setCustomization(designCustomization);
    setSelectedDesignId(design.id);
  };

  const handleDeleteDesign = (id: string) => {
    if (window.confirm('Are you sure you want to delete this design?')) {
      storageUtils.deleteDesign(id);
      setSavedDesigns(storageUtils.getDesigns());
      if (selectedDesignId === id) {
        setSelectedDesignId(null);
      }
    }
  };

  const handleRandomDesign = () => {
    const hullColors = ['#8B4513', '#654321', '#2C2C2C', '#8B0000', '#191970'];
    const hullStyles: Array<'merchant' | 'galleon' | 'brigantine'> = ['merchant', 'galleon', 'brigantine'];
    const sailColors = ['#f0f0f0', '#fffacd', '#ffe4b5', '#ffd700', '#f5f5dc'];
    const sailPatterns: Array<'plain' | 'striped' | 'skull'> = ['plain', 'striped', 'skull'];
    const figureheads: Array<'skull' | 'dragon' | 'crown' | 'anchor'> = ['skull', 'dragon', 'crown', 'anchor'];
    const flagColors = ['#000000', '#8B0000', '#191970', '#2F4F4F'];
    const flagPatterns: Array<'jollyroger' | 'crossbones' | 'stripes'> = ['jollyroger', 'crossbones', 'stripes'];

    setCustomization({
      hullColor: hullColors[Math.floor(Math.random() * hullColors.length)],
      hullStyle: hullStyles[Math.floor(Math.random() * hullStyles.length)],
      sailColor: sailColors[Math.floor(Math.random() * sailColors.length)],
      sailPattern: sailPatterns[Math.floor(Math.random() * sailPatterns.length)],
      figurehead: figureheads[Math.floor(Math.random() * figureheads.length)],
      flagColor: flagColors[Math.floor(Math.random() * flagColors.length)],
      flagPattern: flagPatterns[Math.floor(Math.random() * flagPatterns.length)],
    });
    setSelectedDesignId(null);
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>⚓ Pirate Ship Designer ⚓</h1>
        <p>Shiver their timbers with your own fearsome pirate ship</p>
      </header>

      <main className="app-container">
        <div className="main-content">
          <div className="canvas-wrapper">
            <ShipCanvas customization={customization} />
          </div>

          <ControlPanel customization={customization} onChange={setCustomization} />
        </div>

        <div className="sidebar">
          <div className="action-buttons">
            <button onClick={handleRandomDesign} className="btn btn-primary">
              🎲 Random Design
            </button>
          </div>

          <div className="save-section">
            <h3>Save Design</h3>
            <input
              type="text"
              placeholder="Enter design name..."
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveDesign();
                }
              }}
              className="design-name-input"
            />
            <button onClick={handleSaveDesign} className="btn btn-success">
              💾 Save Design
            </button>
          </div>

          <div className="designs-list">
            <h3>Saved Designs ({savedDesigns.length})</h3>
            {savedDesigns.length === 0 ? (
              <p className="empty-message">No saved designs yet. Create one!</p>
            ) : (
              <div className="designs-scroll">
                {savedDesigns.map((design) => (
                  <div
                    key={design.id}
                    className={`design-item ${selectedDesignId === design.id ? 'active' : ''}`}
                  >
                    <div className="design-header">
                      <span className="design-name">{design.name}</span>
                      <button
                        onClick={() => handleDeleteDesign(design.id)}
                        className="btn-delete"
                        title="Delete design"
                      >
                        ✕
                      </button>
                    </div>
                    <button
                      onClick={() => handleLoadDesign(design)}
                      className="btn btn-load"
                    >
                      Load
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
